import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useAuthStore } from '../../stores/auth.store';
import {
  RefreshCw, AlertTriangle, CheckCircle, DollarSign, Users, Clock,
  ArrowUpCircle, ArrowDownCircle, Search, CreditCard, Info,
} from 'lucide-react';

interface Overview {
  anypayBalance: number;
  ledgerTotal: number;
  platformRevenue: number;
  revenueBreakdown: {
    cashInFee: { total: number; count: number; label: string };
    membershipFee: { total: number; count: number; label: string };
  };
  cashOut: {
    total: number;
    count: number;
    feePerTransaction: number;
    totalFees: number;
    label: string;
  };
  manualCredits: { total: number; count: number; label: string };
  obligations: number;
  difference: number;
  differenceExplained: {
    cashOutFees: number;
    manualCreditsImpact: number;
    total: number;
    note: string;
  };
  pendingCharges: number;
  unmatchedPayments: number;
  lastCronRun: { at: string; credited: number; skipped: number; errors: number } | null;
}

interface PendingData {
  pendingCharges: any[];
  unmatchedPayments: any[];
}

interface Account {
  id: string;
  userName: string;
  userCpf: string;
  userEmail: string;
  balance: string;
  available: string;
}

const fmt = (cents: number) => `R$ ${(Math.abs(cents) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

export default function ReconciliationPage() {
  const queryClient = useQueryClient();
  const isDark = document.documentElement.classList.contains('dark');
  const { isSuperAdmin } = useAuthStore();
  const superAdmin = isSuperAdmin?.() ?? false;
  const [tab, setTab] = useState<'overview' | 'pending' | 'credit' | 'refund' | 'cross' | 'history'>(superAdmin ? 'overview' : 'pending');
  const [searchAccount, setSearchAccount] = useState('');
  const [pendingFilter, setPendingFilter] = useState<'all' | 'unmatched' | 'active' | 'expired'>('all');

  // Credit form
  const [creditVaId, setCreditVaId] = useState('');
  const [creditAmount, setCreditAmount] = useState('');
  const [creditDesc, setCreditDesc] = useState('');
  const [creditApplyFee, setCreditApplyFee] = useState(true);
  const [creditSelectedName, setCreditSelectedName] = useState('');

  // Refund form
  const [refundVaId, setRefundVaId] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  const [refundCorrelation, setRefundCorrelation] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [refundSelectedName, setRefundSelectedName] = useState('');

  const { data: overview, isLoading: loadingOverview } = useQuery<Overview>({
    queryKey: ['reconciliation-overview'],
    queryFn: () => api.get('/admin/reconciliation/overview').then(r => r.data),
  });

  const { data: pending } = useQuery<PendingData>({
    queryKey: ['reconciliation-pending'],
    queryFn: () => api.get('/admin/reconciliation/pending').then(r => r.data),
    enabled: tab === 'pending',
  });

  const { data: accounts } = useQuery<{ accounts: Account[] }>({
    queryKey: ['reconciliation-accounts'],
    queryFn: () => api.get('/admin/reconciliation/accounts').then(r => r.data),
    enabled: tab === 'credit' || tab === 'refund',
  });

  const { data: history } = useQuery<{ items: any[] }>({
    queryKey: ['reconciliation-history'],
    queryFn: () => api.get('/admin/reconciliation/history').then(r => r.data),
    enabled: tab === 'history',
  });

  const { data: crossPayments } = useQuery<{ items: any[]; total: number }>({
    queryKey: ['reconciliation-cross-payments'],
    queryFn: () => api.get('/admin/reconciliation/cross-payments').then(r => r.data),
    enabled: tab === 'cross',
  });

  const creditMutation = useMutation({
    mutationFn: (data: any) => api.post('/admin/reconciliation/credit', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reconciliation-overview'] });
      setCreditVaId(''); setCreditAmount(''); setCreditDesc(''); setCreditSelectedName('');
      alert('Crédito aplicado com sucesso');
    },
    onError: (e: any) => alert(e.response?.data?.message || 'Erro ao creditar'),
  });

  const refundMutation = useMutation({
    mutationFn: (data: any) => api.post('/admin/reconciliation/refund-cashout', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reconciliation-overview'] });
      setRefundVaId(''); setRefundAmount(''); setRefundCorrelation(''); setRefundReason(''); setRefundSelectedName('');
      alert('Estorno aplicado com sucesso');
    },
    onError: (e: any) => alert(e.response?.data?.message || 'Erro ao estornar'),
  });

  const filteredAccounts = accounts?.accounts?.filter(a =>
    !searchAccount || a.userName.toLowerCase().includes(searchAccount.toLowerCase()) ||
    a.userCpf.includes(searchAccount) || a.userEmail.toLowerCase().includes(searchAccount.toLowerCase())
  ) || [];

  // Filtrar pendentes por status
  const filteredPendingCharges = pending?.pendingCharges?.filter(c => {
    if (pendingFilter === 'all') return true;
    if (pendingFilter === 'active') return c.status === 'ACTIVE';
    if (pendingFilter === 'expired') return c.status === 'EXPIRED';
    return false;
  }) || [];

  const cardClass = `rounded-xl p-4 ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'} shadow-sm`;
  const inputClass = `w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`;
  const btnPrimary = 'px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50';
  const btnTab = (active: boolean) => `px-4 py-2 rounded-lg text-sm font-medium transition-colors ${active ? 'bg-blue-600 text-white' : isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`;
  const btnFilter = (active: boolean) => `px-3 py-1 rounded text-xs font-medium ${active ? 'bg-blue-500 text-white' : isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`;

  const actionLabels: Record<string, { label: string; color: string }> = {
    'ADMIN_MANUAL_CREDIT': { label: 'Crédito Manual', color: 'bg-green-100 text-green-800' },
    'ADMIN_CASHOUT_REFUND': { label: 'Estorno Cash-out', color: 'bg-orange-100 text-orange-800' },
    'PIX_RECONCILIATION_CREDIT': { label: 'Crédito CRON', color: 'bg-blue-100 text-blue-800' },
    'PIX_INCREMENTAL_RECONCILIATION': { label: 'Execução CRON', color: 'bg-gray-100 text-gray-800' },
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Conciliação Financeira</h1>
        <button onClick={() => { queryClient.invalidateQueries({ queryKey: ['reconciliation-overview'] }); queryClient.invalidateQueries({ queryKey: ['reconciliation-pending'] }); }}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
          <RefreshCw className="w-4 h-4" /> Atualizar
        </button>
      </div>

      <div className="flex gap-2 flex-wrap">
        {superAdmin && <button className={btnTab(tab === 'overview')} onClick={() => setTab('overview')}>Visão Geral</button>}
        <button className={btnTab(tab === 'pending')} onClick={() => setTab('pending')}>Pendentes</button>
        <button className={btnTab(tab === 'credit')} onClick={() => setTab('credit')}>Creditar Conta</button>
        <button className={btnTab(tab === 'refund')} onClick={() => setTab('refund')}>Estornar Saque</button>
        {superAdmin && <button className={btnTab(tab === 'cross')} onClick={() => setTab('cross')}>Pagamentos Cruzados</button>}
        <button className={btnTab(tab === 'history')} onClick={() => setTab('history')}>Histórico</button>
      </div>

      {/* === OVERVIEW === */}
      {tab === 'overview' && (
        <div className="space-y-4">
          {loadingOverview ? (
            <div className={`${cardClass} animate-pulse h-32`} />
          ) : overview ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className={cardClass}>
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="w-5 h-5 text-green-500" />
                    <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Saldo AnyPayBR</span>
                  </div>
                  <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{fmt(overview.anypayBalance)}</p>
                  <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Saldo real no gateway</p>
                </div>
                <div className={cardClass}>
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-5 h-5 text-blue-500" />
                    <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Saldo Correntistas</span>
                  </div>
                  <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{fmt(overview.ledgerTotal)}</p>
                  <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Soma dos saldos de todos os clientes</p>
                </div>
                <div className={cardClass}>
                  <div className="flex items-center gap-2 mb-2">
                    <CreditCard className="w-5 h-5 text-purple-500" />
                    <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Receita Plataforma</span>
                  </div>
                  <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{fmt(overview.platformRevenue)}</p>
                  <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Taxas 5% + mensalidades</p>
                </div>
                <div className={cardClass}>
                  <div className="flex items-center gap-2 mb-2">
                    {overview.difference >= 0 ? <CheckCircle className="w-5 h-5 text-green-500" /> : <AlertTriangle className="w-5 h-5 text-yellow-500" />}
                    <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Diferença</span>
                  </div>
                  <p className={`text-xl font-bold ${overview.difference >= 0 ? 'text-green-500' : 'text-yellow-500'}`}>
                    {overview.difference < 0 ? '-' : ''}{fmt(overview.difference)}
                  </p>
                  <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    {overview.difference < 0 ? 'Inclui taxas de saque AnyPayBR (R$1,20/saque)' : 'Saldo positivo'}
                  </p>
                </div>
              </div>

              {/* Info box explicando a diferença */}
              {overview.difference < 0 && (
                <div className={`${cardClass} ${isDark ? 'bg-yellow-900/20 border-yellow-700' : 'bg-yellow-50 border-yellow-200'}`}>
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                    <div className={`text-sm ${isDark ? 'text-yellow-200' : 'text-yellow-800'}`}>
                      <p className="font-medium mb-2">Decomposição da diferença de {fmt(Math.abs(overview.difference))}:</p>
                      <div className="space-y-1">
                        <p>• Custo de saques AnyPayBR: {overview.cashOut.count} saques × R$ 1,20 = <span className="font-semibold">{fmt(overview.cashOut.totalFees)}</span></p>
                        <p>• Créditos manuais (reconciliações): {overview.manualCredits.count} operações = <span className="font-semibold">{fmt(overview.manualCredits.total)}</span></p>
                        <p className="text-xs mt-2 opacity-75">Os créditos manuais são depósitos reais que estavam na AnyPayBR mas não foram processados automaticamente. Depois que o correntista sacou, o dinheiro saiu da AnyPayBR.</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Decomposição da Receita */}
              <div>
                <h3 className={`text-sm font-semibold mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>RECEITA DA PLATAFORMA</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className={cardClass}>
                    <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Taxa cash-in 5%</p>
                    <p className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-300'}`}>3% AnyPayBR + 2% Finteck</p>
                    <p className={`text-lg font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{fmt(overview.revenueBreakdown.cashInFee.total)}</p>
                    <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{overview.revenueBreakdown.cashInFee.count} depósitos</p>
                  </div>
                  <div className={cardClass}>
                    <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Mensalidade R$ 21</p>
                    <p className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-300'}`}>Cobrada dia 5 de cada mês</p>
                    <p className={`text-lg font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{fmt(overview.revenueBreakdown.membershipFee.total)}</p>
                    <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{overview.revenueBreakdown.membershipFee.count} cobranças</p>
                  </div>
                  <div className={cardClass}>
                    <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Custo saques AnyPayBR</p>
                    <p className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-300'}`}>R$ 1,20 por saque PIX</p>
                    <p className={`text-lg font-bold mt-1 text-red-500`}>-{fmt(overview.cashOut.totalFees)}</p>
                    <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{overview.cashOut.count} saques (total sacado: {fmt(overview.cashOut.total)})</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className={cardClass}>
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="w-4 h-4 text-yellow-500" />
                    <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Cobranças Ativas</span>
                  </div>
                  <p className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{overview.pendingCharges}</p>
                  <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>QR codes aguardando pagamento</p>
                </div>
                <div className={cardClass}>
                  <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle className="w-4 h-4 text-orange-500" />
                    <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Pagamentos Não Vinculados</span>
                  </div>
                  <p className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{overview.unmatchedPayments}</p>
                  <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Webhooks sem match (histórico total)</p>
                </div>
                <div className={cardClass}>
                  <div className="flex items-center gap-2 mb-1">
                    <RefreshCw className="w-4 h-4 text-blue-500" />
                    <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Última Reconciliação Automática</span>
                  </div>
                  <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {overview.lastCronRun ? (
                      <>
                        {new Date(overview.lastCronRun.at).toLocaleString('pt-BR')}
                        <span className="block text-xs mt-1">{overview.lastCronRun.credited} creditados, {overview.lastCronRun.skipped} ignorados</span>
                      </>
                    ) : 'Aguardando primeira execução'}
                  </p>
                </div>
              </div>
            </>
          ) : null}
        </div>
      )}

      {/* === PENDING === */}
      {tab === 'pending' && pending && (
        <div className="space-y-6">
          {/* Filtros de status */}
          <div className="flex gap-2 items-center">
            <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Filtrar:</span>
            <button className={btnFilter(pendingFilter === 'all')} onClick={() => setPendingFilter('all')}>Todos</button>
            <button className={btnFilter(pendingFilter === 'unmatched')} onClick={() => setPendingFilter('unmatched')}>
              Não Vinculados ({pending.unmatchedPayments.length})
            </button>
            <button className={btnFilter(pendingFilter === 'active')} onClick={() => setPendingFilter('active')}>
              Ativos ({pending.pendingCharges.filter((c: any) => c.status === 'ACTIVE').length})
            </button>
            <button className={btnFilter(pendingFilter === 'expired')} onClick={() => setPendingFilter('expired')}>
              Expirados ({pending.pendingCharges.filter((c: any) => c.status === 'EXPIRED').length})
            </button>
          </div>

          {/* UNMATCHED */}
          {(pendingFilter === 'all' || pendingFilter === 'unmatched') && pending.unmatchedPayments.length > 0 && (
            <div>
              <h2 className={`text-lg font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Pagamentos Não Vinculados ({pending.unmatchedPayments.length})
              </h2>
              <p className={`text-xs mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                Webhooks que chegaram mas não foram vinculados a nenhuma cobrança. A maioria já foi creditada via polling ou reconciliação.
              </p>
              <div className="overflow-x-auto">
                <table className={`w-full text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  <thead className={isDark ? 'bg-gray-700' : 'bg-gray-50'}>
                    <tr>
                      <th className="px-3 py-2 text-left">Cliente</th>
                      <th className="px-3 py-2 text-left">CPF/CNPJ</th>
                      <th className="px-3 py-2 text-right">Valor</th>
                      <th className="px-3 py-2 text-left">Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pending.unmatchedPayments.map((u: any) => (
                      <tr key={u.id} className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                        <td className="px-3 py-2">{u.customerName}</td>
                        <td className="px-3 py-2 font-mono text-xs">{u.customerDocument}</td>
                        <td className="px-3 py-2 text-right font-semibold">{fmt(u.amount * 100)}</td>
                        <td className="px-3 py-2 text-xs">{new Date(u.createdAt).toLocaleString('pt-BR')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Cobranças pendentes */}
          {(pendingFilter === 'all' || pendingFilter === 'active' || pendingFilter === 'expired') && filteredPendingCharges.length > 0 && (
            <div>
              <h2 className={`text-lg font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Cobranças {pendingFilter === 'active' ? 'Ativas' : pendingFilter === 'expired' ? 'Expiradas' : 'Pendentes'} ({filteredPendingCharges.length})
              </h2>
              <p className={`text-xs mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                {pendingFilter === 'expired' 
                  ? 'QR codes que expiraram sem pagamento. Normal — cliente gerou mas não pagou a tempo.'
                  : 'QR codes aguardando pagamento ou que podem ter sido pagos sem atualização no sistema.'}
              </p>
              <div className="overflow-x-auto">
                <table className={`w-full text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  <thead className={isDark ? 'bg-gray-700' : 'bg-gray-50'}>
                    <tr>
                      <th className="px-3 py-2 text-left">Cliente</th>
                      <th className="px-3 py-2 text-right">Valor</th>
                      <th className="px-3 py-2 text-center">Status</th>
                      <th className="px-3 py-2 text-left">Criada em</th>
                      <th className="px-3 py-2 text-left">Expira em</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPendingCharges.map((c: any) => (
                      <tr key={c.id} className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                        <td className="px-3 py-2">{c.customerName || c.customerTaxId || 'Sem identificação'}</td>
                        <td className="px-3 py-2 text-right font-semibold">{fmt(Number(c.value))}</td>
                        <td className="px-3 py-2 text-center">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            c.status === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                          }`}>{c.status === 'ACTIVE' ? 'Ativo' : 'Expirado'}</span>
                        </td>
                        <td className="px-3 py-2 text-xs">{new Date(c.createdAt).toLocaleString('pt-BR')}</td>
                        <td className="px-3 py-2 text-xs">{c.expiresAt ? new Date(c.expiresAt).toLocaleString('pt-BR') : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {pending.unmatchedPayments.length === 0 && filteredPendingCharges.length === 0 && (
            <div className={`${cardClass} text-center py-8`}>
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>Nenhum item pendente neste filtro</p>
            </div>
          )}
        </div>
      )}

      {/* === CREDIT === */}
      {tab === 'credit' && (
        <div className={cardClass}>
          <h2 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <ArrowUpCircle className="w-5 h-5 inline mr-2 text-green-500" />
            Creditar Conta (Depósito não processado)
          </h2>
          <p className={`text-xs mb-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            Use para creditar um depósito que foi confirmado na AnyPayBR mas não apareceu no saldo do correntista.
          </p>
          <div className="mb-4">
            <label className={`block text-sm mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Buscar correntista</label>
            <div className="flex items-center gap-2">
              <Search className="w-4 h-4 flex-shrink-0" />
              <input type="text" placeholder="Nome, CPF ou email..." value={searchAccount}
                onChange={e => { setSearchAccount(e.target.value); setCreditVaId(''); setCreditSelectedName(''); }} className={inputClass} />
            </div>
            {searchAccount && !creditVaId && filteredAccounts.length > 0 && (
              <div className={`mt-1 max-h-48 overflow-y-auto rounded-lg border ${isDark ? 'border-gray-600 bg-gray-800' : 'border-gray-200 bg-white'}`}>
                {filteredAccounts.slice(0, 10).map(a => (
                  <button key={a.id} onClick={() => { setCreditVaId(a.id); setSearchAccount(a.userName); setCreditSelectedName(a.userName); }}
                    className={`w-full text-left px-3 py-2 text-sm border-b last:border-b-0 ${isDark ? 'border-gray-700 hover:bg-gray-700' : 'border-gray-100 hover:bg-blue-50'}`}>
                    <span className="font-medium">{a.userName}</span>
                    <span className={`ml-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{a.userCpf}</span>
                    <span className={`float-right text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Saldo: {fmt(Number(a.balance))}</span>
                  </button>
                ))}
              </div>
            )}
            {creditSelectedName && <p className={`mt-1 text-sm text-green-600`}>Selecionado: {creditSelectedName}</p>}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className={`block text-sm mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Valor (R$)</label>
              <input type="number" step="0.01" min="0.01" placeholder="100.00" value={creditAmount}
                onChange={e => setCreditAmount(e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={`block text-sm mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Descrição (motivo)</label>
              <input type="text" placeholder="PIX recebido - depósito de 30/03" value={creditDesc}
                onChange={e => setCreditDesc(e.target.value)} className={inputClass} />
            </div>
          </div>
          <div className="flex items-center gap-4 mb-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={creditApplyFee} onChange={e => setCreditApplyFee(e.target.checked)} className="w-4 h-4 rounded" />
              <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Aplicar taxa de cash-in (5%)</span>
            </label>
          </div>
          <button onClick={() => {
            if (!creditVaId || !creditAmount || !creditDesc) return alert('Preencha todos os campos e selecione um correntista');
            if (!confirm(`Confirma crédito de R$ ${creditAmount} para ${creditSelectedName}?${creditApplyFee ? ' (com taxa 5%)' : ' (sem taxa)'}`)) return;
            creditMutation.mutate({ virtualAccountId: creditVaId, amountCents: Math.round(parseFloat(creditAmount) * 100), description: creditDesc, applyFee: creditApplyFee });
          }} disabled={creditMutation.isPending || !creditVaId} className={btnPrimary}>
            {creditMutation.isPending ? 'Processando...' : 'Creditar'}
          </button>
        </div>
      )}

      {/* === REFUND === */}
      {tab === 'refund' && (
        <div className={cardClass}>
          <h2 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <ArrowDownCircle className="w-5 h-5 inline mr-2 text-orange-500" />
            Estornar Saque Devolvido
          </h2>
          <p className={`text-xs mb-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            Use quando um saque PIX foi devolvido pela AnyPayBR (chave inválida, conta destino com problema) e o valor precisa voltar para o correntista.
          </p>
          <div className="mb-4">
            <label className={`block text-sm mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Buscar correntista</label>
            <div className="flex items-center gap-2">
              <Search className="w-4 h-4 flex-shrink-0" />
              <input type="text" placeholder="Nome, CPF ou email..." value={searchAccount}
                onChange={e => { setSearchAccount(e.target.value); setRefundVaId(''); setRefundSelectedName(''); }} className={inputClass} />
            </div>
            {searchAccount && !refundVaId && filteredAccounts.length > 0 && (
              <div className={`mt-1 max-h-48 overflow-y-auto rounded-lg border ${isDark ? 'border-gray-600 bg-gray-800' : 'border-gray-200 bg-white'}`}>
                {filteredAccounts.slice(0, 10).map(a => (
                  <button key={a.id} onClick={() => { setRefundVaId(a.id); setSearchAccount(a.userName); setRefundSelectedName(a.userName); }}
                    className={`w-full text-left px-3 py-2 text-sm border-b last:border-b-0 ${isDark ? 'border-gray-700 hover:bg-gray-700' : 'border-gray-100 hover:bg-blue-50'}`}>
                    <span className="font-medium">{a.userName}</span>
                    <span className={`ml-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{a.userCpf}</span>
                    <span className={`float-right text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Saldo: {fmt(Number(a.balance))}</span>
                  </button>
                ))}
              </div>
            )}
            {refundSelectedName && <p className={`mt-1 text-sm text-green-600`}>Selecionado: {refundSelectedName}</p>}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className={`block text-sm mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Valor do estorno (R$)</label>
              <input type="number" step="0.01" min="0.01" placeholder="100.00" value={refundAmount}
                onChange={e => setRefundAmount(e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={`block text-sm mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>ID da transação original</label>
              <input type="text" placeholder="banking-send-xxx-xxx" value={refundCorrelation}
                onChange={e => setRefundCorrelation(e.target.value)} className={inputClass} />
            </div>
          </div>
          <div className="mb-4">
            <label className={`block text-sm mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Motivo do estorno</label>
            <input type="text" placeholder="PIX devolvido - chave destino inválida" value={refundReason}
              onChange={e => setRefundReason(e.target.value)} className={inputClass} />
          </div>
          <button onClick={() => {
            if (!refundVaId || !refundAmount || !refundCorrelation || !refundReason) return alert('Preencha todos os campos');
            if (!confirm(`Confirma estorno de R$ ${refundAmount} para ${refundSelectedName}?`)) return;
            refundMutation.mutate({ virtualAccountId: refundVaId, amountCents: Math.round(parseFloat(refundAmount) * 100), originalCorrelationId: refundCorrelation, reason: refundReason });
          }} disabled={refundMutation.isPending || !refundVaId} className={btnPrimary}>
            {refundMutation.isPending ? 'Processando...' : 'Estornar'}
          </button>
        </div>
      )}

      {/* === CROSS PAYMENTS === */}
      {tab === 'cross' && crossPayments && (
        <div>
          <h2 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Pagamentos Cruzados ({crossPayments.total})
          </h2>
          <p className={`text-xs mb-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            Depósitos onde o pagador (quem fez o PIX) é diferente do titular da conta que recebeu o crédito.
            Isso pode indicar que alguém pagou o QR code de outra pessoa por engano.
          </p>
          {crossPayments.items.length > 0 ? (
            <div className="overflow-x-auto">
              <table className={`w-full text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                <thead className={isDark ? 'bg-gray-700' : 'bg-gray-50'}>
                  <tr>
                    <th className="px-3 py-2 text-left">Titular da Conta</th>
                    <th className="px-3 py-2 text-left">Quem Pagou</th>
                    <th className="px-3 py-2 text-right">Valor</th>
                    <th className="px-3 py-2 text-left">Data</th>
                  </tr>
                </thead>
                <tbody>
                  {crossPayments.items.map((cp: any, i: number) => (
                    <tr key={i} className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                      <td className="px-3 py-2">
                        <span className="font-medium">{cp.accountOwner}</span>
                        {cp.ownerCpf && <span className={`block text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{cp.ownerCpf}</span>}
                      </td>
                      <td className="px-3 py-2">
                        <span className="text-orange-500 font-medium">{cp.payerName}</span>
                        {cp.payerDocument && <span className={`block text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{cp.payerDocument}</span>}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold">{fmt(cp.amount)}</td>
                      <td className="px-3 py-2 text-xs">{new Date(cp.createdAt).toLocaleString('pt-BR')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className={`${cardClass} text-center py-8`}>
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>Nenhum pagamento cruzado detectado</p>
            </div>
          )}
        </div>
      )}

      {/* === HISTORY === */}
      {tab === 'history' && history && (
        <div>
          <h2 className={`text-lg font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Histórico de Conciliações ({history.items.length})
          </h2>
          <div className="space-y-2">
            {history.items.map((h: any) => {
              const actionInfo = actionLabels[h.action] || { label: h.action, color: 'bg-gray-100 text-gray-800' };
              return (
                <div key={h.id} className={cardClass}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded ${actionInfo.color}`}>{actionInfo.label}</span>
                      {h.data?.accountHolder && (
                        <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{h.data.accountHolder}</span>
                      )}
                      {h.data?.amount && (
                        <span className="text-sm font-semibold text-green-600">{fmt(h.data.amount)}</span>
                      )}
                      {h.data?.credited !== undefined && (
                        <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {h.data.credited} creditados, {h.data.skipped} ignorados
                        </span>
                      )}
                      {h.data?.description && (
                        <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>— {h.data.description}</span>
                      )}
                    </div>
                    <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      {new Date(h.createdAt).toLocaleString('pt-BR')}
                    </span>
                  </div>
                </div>
              );
            })}
            {history.items.length === 0 && (
              <div className={`${cardClass} text-center py-8`}>
                <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>Nenhuma conciliação registrada ainda</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
